import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  open: "bg-blue-100 text-blue-800", approved: "bg-emerald-100 text-emerald-800",
  booked: "bg-emerald-100 text-emerald-800", declined: "bg-red-100 text-red-700",
  expired: "bg-gray-200 text-gray-600",
};

export default function QuotesTab() {
  const [quotes, setQuotes] = useState(null);
  const load = useCallback(() => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => setQuotes([])), []);
  useEffect(() => { load(); }, [load]);

  const copy = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/q/${token}`);
    toast.success("Quote link copied");
  };

  const open = quotes?.filter((q) => q.status === "open") || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Quotes</CardTitle>
        {quotes && <p className="text-sm text-muted-foreground" data-testid="quotes-open-count">{open.length} outstanding · {quotes.length} total (last 50). Quotes expire 7 days after creation; a reminder text goes out 2 days before expiry.</p>}
      </CardHeader>
      <CardContent>
        {quotes === null ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : quotes.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="quotes-empty">No quotes yet — drivers and admins can create them via the "New quote" button.</p>
        ) : (
          <Table data-testid="admin-quotes-table">
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead><TableHead>Route</TableHead><TableHead className="text-right">Total</TableHead>
                <TableHead>By</TableHead><TableHead>Sent to</TableHead><TableHead>Expires</TableHead>
                <TableHead>Status</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id} data-testid={`admin-quote-row-${q.id}`}>
                  <TableCell>{new Date(q.created_at).toLocaleDateString("en-GB")}</TableCell>
                  <TableCell className="font-medium">{q.tracking_number ? <span className="font-heading font-bold">{q.tracking_number} · </span> : null}{q.pickup.city} → {q.dropoff.city}</TableCell>
                  <TableCell className="text-right font-semibold">${q.price.total.toFixed(2)}</TableCell>
                  <TableCell>{q.created_by_name}</TableCell>
                  <TableCell className="text-muted-foreground">{q.assigned_account_name || q.sent_phone || "—"}</TableCell>
                  <TableCell>{new Date(q.expires_at).toLocaleDateString("en-GB")}</TableCell>
                  <TableCell><Badge className={`${STATUS_STYLE[q.status] || "bg-gray-100 text-gray-700"} capitalize`}>{q.status}</Badge></TableCell>
                  <TableCell>
                    <button onClick={() => copy(q.token)} data-testid={`admin-quote-copy-${q.id}`} className="text-muted-foreground hover:text-kilted-green"><Copy className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
